import { c as _c } from "react/compiler-runtime";
import getCustomAppearance from '@onefootprint/appearance';
import { CLIENT_PUBLIC_KEY_HEADER, checkIsInIframe } from '@onefootprint/core';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import { FootprintPrivateEvent, type FootprintVariant } from '@onefootprint/footprint-js-legacy';
import type { ComponentsSdkContext, IdvCompletePayload } from '@onefootprint/idv';
import Idv, { AppErrorBoundary, ComponentsSdkTypes, checkIsAndroid, getLogger, Logger, SessionExpired, trackAction, useIdentifyValidate, useLogStateMachine } from '@onefootprint/idv';
import { callExternalBack, useFootprintProvider } from '@onefootprint/idv2';
import type { GetServerSideProps } from 'next';
import { useEffect } from 'react';
import useBifrostMachine from 'src/hooks/use-bifrost-machine';
import Layout from '../../components/layout';
import Complete from '../complete';
import Init from '../init';
import InitError from '../init-error';
type RootProps = {
  variant?: FootprintVariant;
};
const {
  logError,
  logInfo,
  logWarn
} = getLogger({
  location: 'bifrost-root'
});
const Root = t0 => {
  const $ = _c(66);
  const {
    variant
  } = t0;
  const fpProvider = useFootprintProvider();
  const [state, send] = useBifrostMachine();
  const {
    bootstrapData,
    l10n,
    showCompletionPage,
    showLogo,
    authToken,
    publicKey,
    isComponentsSdk,
    sandboxOutcome: t1,
    sandboxId,
    shouldRelayToComponents
  } = state.context;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    overallOutcome,
    idDocOutcome
  } = t2;
  let t3;
  if ($[2] !== publicKey) {
    t3 = publicKey ? {
      [CLIENT_PUBLIC_KEY_HEADER]: publicKey
    } : undefined;
    $[2] = publicKey;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const obConfigAuth = t3;
  const isWebview = !checkIsInIframe();
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = !checkIsInIframe() && checkIsAndroid();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const isAndroidWebview = t4;
  const shouldShowCompletionPage = showCompletionPage || isAndroidWebview;
  const mutIdentifyValidate = useIdentifyValidate();
  const {
    getErrorStatusCode
  } = useRequestError();
  useLogStateMachine("bifrost", state);
  let t5;
  if ($[5] !== fpProvider || $[6] !== getErrorStatusCode || $[7] !== mutIdentifyValidate) {
    t5 = args => {
      mutIdentifyValidate.mutate({
        authToken: args.authToken
      }, {
        onError: err => {
          if (getErrorStatusCode(err) === 403) {
            logWarn("No identify validation token for a session without a user", err);
            return;
          }
          logError("Error while validating auth token", err);
        },
        onSuccess: res => fpProvider.auth(res.validationToken)
      });
    };
    $[5] = fpProvider;
    $[6] = getErrorStatusCode;
    $[7] = mutIdentifyValidate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleIdentifyCompletion = t5;
  let t6;
  if ($[9] !== fpProvider || $[10] !== send || $[11] !== shouldShowCompletionPage) {
    t6 = t7 => {
      const {
        validationToken,
        authToken: idvAuthToken,
        deviceResponseJson,
        hasCompletionScreen
      } = t7;
      logInfo("IDV flow is complete, sending validation token back to the tenant");
      Logger.appendGlobalContext({
        idv_outcome: "complete"
      });
      trackAction("idv:outcome", {
        outcome: "complete"
      });
      Logger.stopSessionReplay();
      if (validationToken) {
        if (!shouldShowCompletionPage) {
          fpProvider.complete({
            validationToken,
            deviceResponse: deviceResponseJson,
            authToken: idvAuthToken,
            autoClose: !hasCompletionScreen
          });
          return;
        }
        send({
          type: "idvComplete",
          payload: {
            validationToken,
            deviceResponseJson,
            authToken: idvAuthToken
          }
        });
      }
    };
    $[9] = fpProvider;
    $[10] = send;
    $[11] = shouldShowCompletionPage;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleBifrostCompletion = t6;
  let t7;
  if ($[13] !== fpProvider) {
    t7 = () => {
      logInfo("Completion screen closed the SDK on its own");
      fpProvider.close();
    };
    $[13] = fpProvider;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleAutoAdvance = t7;
  let t8;
  if ($[15] !== fpProvider) {
    t8 = () => {
      logInfo("IDV flow is closed by the user");
      Logger.appendGlobalContext({
        idv_outcome: "closed"
      });
      trackAction("idv:outcome", {
        outcome: "closed"
      });
      Logger.stopSessionReplay();
      fpProvider.cancel();
      fpProvider.close();
    };
    $[15] = fpProvider;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleClose = t8;
  let t10;
  let t9;
  if ($[17] !== fpProvider) {
    t9 = () => {
      const unsubscribe = fpProvider.on(FootprintPrivateEvent.back, callExternalBack);
      return unsubscribe;
    };
    t10 = [fpProvider];
    $[17] = fpProvider;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  if ($[20] !== fpProvider) {
    t11 = cb => fpProvider.on(FootprintPrivateEvent.back, cb);
    $[20] = fpProvider;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleExternalBack = t11;
  let componentsSdkContext;
  if (isComponentsSdk) {
    let t12;
    if ($[22] !== fpProvider) {
      t12 = cb_0 => fpProvider.on(FootprintPrivateEvent.relayFromComponents, cb_0);
      $[22] = fpProvider;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    const t13 = !shouldRelayToComponents;
    let t14;
    if ($[24] !== fpProvider.relayToComponents || $[25] !== t12 || $[26] !== t13) {
      t14 = {
        onRelayFromComponents: t12,
        relayToComponents: fpProvider.relayToComponents,
        componentsSdkType: isWebview ? ComponentsSdkTypes.MOBILE : ComponentsSdkTypes.WEB,
        skipRelayToComponents: t13
      };
      $[24] = fpProvider.relayToComponents;
      $[25] = t12;
      $[26] = t13;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    componentsSdkContext = t14;
  }
  let t12;
  if ($[28] !== send) {
    t12 = () => send({
      type: "reset"
    });
    $[28] = send;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== state) {
    t13 = state.matches("init") && <Init />;
    $[30] = state;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== state) {
    t14 = state.matches("sessionExpired") && <SessionExpired />;
    $[32] = state;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== state) {
    t15 = state.matches("initError") && <InitError />;
    $[34] = state;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== authToken || $[37] !== bootstrapData || $[38] !== componentsSdkContext || $[39] !== fpProvider.backButtonVisibilityChange || $[40] !== fpProvider.requirementChanged || $[41] !== handleAutoAdvance || $[42] !== handleBifrostCompletion || $[43] !== handleClose || $[44] !== handleExternalBack || $[45] !== handleIdentifyCompletion || $[46] !== idDocOutcome || $[47] !== l10n || $[48] !== obConfigAuth || $[49] !== overallOutcome || $[50] !== sandboxId || $[51] !== showLogo || $[52] !== state) {
    t16 = state.matches("idv") && <Idv authToken={authToken} obConfigAuth={obConfigAuth} bootstrapData={bootstrapData} onComplete={handleBifrostCompletion} onClose={handleClose} autoAdvance={{
      mode: "close",
      onDone: handleAutoAdvance
    }} onIdentifyDone={handleIdentifyCompletion} showLogo={showLogo} componentsSdkContext={componentsSdkContext} overallOutcome={overallOutcome} idDocOutcome={idDocOutcome} onBackButtonVisibilityChange={fpProvider.backButtonVisibilityChange} onRequirementChange={fpProvider.requirementChanged} onExternalBack={handleExternalBack} l10n={l10n} sandboxId={sandboxId} />;
    $[36] = authToken;
    $[37] = bootstrapData;
    $[38] = componentsSdkContext;
    $[39] = fpProvider.backButtonVisibilityChange;
    $[40] = fpProvider.requirementChanged;
    $[41] = handleAutoAdvance;
    $[42] = handleBifrostCompletion;
    $[43] = handleClose;
    $[44] = handleExternalBack;
    $[45] = handleIdentifyCompletion;
    $[46] = idDocOutcome;
    $[47] = l10n;
    $[48] = obConfigAuth;
    $[49] = overallOutcome;
    $[50] = sandboxId;
    $[51] = showLogo;
    $[52] = state;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== state) {
    t17 = state.matches("complete") && <Complete />;
    $[54] = state;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t12 || $[57] !== t13 || $[58] !== t14 || $[59] !== t15 || $[60] !== t16 || $[61] !== t17) {
    t18 = <AppErrorBoundary onReset={t12}>{t13}{t14}{t15}{t16}{t17}</AppErrorBoundary>;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t15;
    $[60] = t16;
    $[61] = t17;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== t18 || $[64] !== variant) {
    t19 = <Layout variant={variant}>{t18}</Layout>;
    $[63] = t18;
    $[64] = variant;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  return t19;
};
export const getServerSideProps: GetServerSideProps = async ({
  query
}) => {
  const obConfig = query.public_key as string | undefined;
  const language = query.lng as string | undefined;
  const params = query as Record<string, string>;
  const response = await getCustomAppearance({
    strategy: ['queryParameters', 'obConfig'],
    obConfig,
    params,
    variant: params.variant
  });
  const isVariablesEmpty = !response.variables || Object.keys(response.variables).length === 0;
  const isRulesEmpty = !response.rules || Object.keys(response.rules).length === 0;
  const hasThemePreference = ['dark', 'system'].includes(response.appearance?.theme ?? '');
  const isThemeEmpty = isRulesEmpty && isVariablesEmpty && !hasThemePreference;
  return {
    props: {
      ...response,
      theme: isThemeEmpty ? null : response.theme,
      language: language ?? 'en'
    }
  };
};
export default Root;